import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import { toast } from "react-toastify";
import MemberDetailsModal from "./MemberDetailsModal";
import axiosInstance from "../../../../api/axiosConfig";

function Members() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal States
  const [selectedMember, setSelectedMember] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  // Pagination States
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  // Fetch Members
  const fetchMembers = useCallback(
    async (currentPage) => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          "/marketplace/getequity/get-members",
          {
            params: {
              page: currentPage,
              limit: limit,
            },
          },
        );

        if (response.data?.status === "successful") {
          setMembers(response.data.data || []);
          setTotalPages(response.data.totalPages || 1);
        }
      } catch (error) {
        console.error("Error fetching members: ", error);
        toast.error(error.response?.data?.message || "Failed to load members.");
      } finally {
        setLoading(false);
      }
    },
    [limit],
  );

  useEffect(() => {
    fetchMembers(page);
  }, [page, fetchMembers]);

  // Handle member row click
  const handleMemberClick = (member) => {
    setSelectedMember(member);
    setOpenModal(true);
  };

  // Client-side search filtering across Name, Email, & Phone
  const filteredMembers = members.filter((member) => {
    const user = member.user || {};
    const fullName = `${user.fname || ""} ${user.lname || ""}`.toLowerCase();
    const email = user.email?.toLowerCase() || "";
    const phone = user.phone?.toLowerCase() || "";
    const searchLower = searchTerm.toLowerCase();

    return (
      fullName.includes(searchLower) ||
      email.includes(searchLower) ||
      phone.includes(searchLower)
    );
  });

  return (
    <Box
      sx={{ p: 3, bgcolor: "#ffffff", color: "#000000", minHeight: "100vh" }}
    >
      {/* Header & Search Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}
        >
          Members
        </Typography>

        <TextField
          size="small"
          placeholder="Search by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#000000" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: "100%", sm: 340 },
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              bgcolor: "#ffffff",
              "& fieldset": { borderColor: "#000000", borderWidth: "1px" },
              "&:hover fieldset": { borderColor: "#000000" },
              "&.Mui-focused fieldset": {
                borderColor: "#000000",
                borderWidth: "2px",
              },
            },
          }}
        />
      </Box>

      {/* Members Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "2px solid #000000",
          borderRadius: 0,
          bgcolor: "#ffffff",
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#000000" }}>
              <TableCell sx={{ color: "#ffffff", fontWeight: 700, width: 60 }}>
                User
              </TableCell>
              <TableCell sx={{ color: "#ffffff", fontWeight: 700 }}>
                Member Name
              </TableCell>
              <TableCell sx={{ color: "#ffffff", fontWeight: 700 }}>
                Email Address
              </TableCell>
              <TableCell sx={{ color: "#ffffff", fontWeight: 700 }}>
                Phone Number
              </TableCell>
              <TableCell sx={{ color: "#ffffff", fontWeight: 700 }}>
                Status
              </TableCell>
              <TableCell sx={{ color: "#ffffff", fontWeight: 700 }}>
                Joined At
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress sx={{ color: "#000000" }} />
                </TableCell>
              </TableRow>
            ) : filteredMembers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No members found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredMembers.map((member) => {
                const user = member.user || {};
                const fullName =
                  user.fname || user.lname
                    ? `${user.fname || ""} ${user.lname || ""}`.trim()
                    : "N/A";

                return (
                  <TableRow
                    key={member._id}
                    onClick={() => handleMemberClick(member)}
                    sx={{
                      cursor: "pointer",
                      transition: "background-color 0.15s ease",
                      "&:hover": { bgcolor: "#f0f0f0" },
                      "&:last-child td, &:last-child th": { border: 0 },
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: "#000000",
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PersonIcon fontSize="small" />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#000000" }}>
                      {fullName}
                    </TableCell>
                    <TableCell sx={{ color: "#333333" }}>
                      {user.email || "N/A"}
                    </TableCell>
                    <TableCell sx={{ color: "#333333" }}>
                      {user.phone || "N/A"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={member.status || "Unknown"}
                        size="small"
                        sx={{
                          borderRadius: 0,
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          bgcolor:
                            member.status?.toLowerCase() === "active"
                              ? "#000000"
                              : "#e0e0e0",
                          color:
                            member.status?.toLowerCase() === "active"
                              ? "#ffffff"
                              : "#000000",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "#666666", fontSize: "0.85rem" }}>
                      {member.createdAt
                        ? new Date(member.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )
                        : "N/A"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: 0,
                color: "#000000",
                "&.Mui-selected": {
                  bgcolor: "#000000",
                  color: "#ffffff",
                  "&:hover": { bgcolor: "#333333" },
                },
              },
            }}
          />
        </Box>
      )}

      {/* Member Details Popup Modal */}
      {openModal && (
        <MemberDetailsModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          member={selectedMember}
        />
      )}
    </Box>
  );
}

export default Members;
